import { CommonModalWithSidebar } from '@sketch-note/design-system/src/components/modal';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'src/shared/hooks/use-auth';

import { Account, WorkspaceProps } from './account';
import { Availability } from './availability';
import { Billing } from './billing';
import { Integrations, SlackPage } from './integrations';
import { GithubPage } from './integrations/github';
import { GooglePage } from './integrations/google';
import { ManageMembers, Member } from './manage-members';
import { Preferences } from './preferences';
import { Profile } from './profile';
import {
  useAvailabilityStore,
  useManageMembersStore,
  usePreferencesStore,
  useProfileStore,
} from './store';
import { useAccountStore } from './store/useAccountStore';

export const Settings = ({
  open,
  currentMenu,
  data,
  onClose,
}: {
  companyName: string;
  workspaces: WorkspaceProps[];
  guests?: Member[];
  open?: boolean;
  data?: any;
  currentMenu?: string;
  onClose: () => void;
}): JSX.Element => {
  const [activeMenu, setActiveMenu] = useState('profile');
  const authData = useAuth((s) => s.userDetails);
  const memberRole = authData?.memberInformation.role;
  const { setProfileValues } = useProfileStore((state) => state);
  const { setHours } = useAvailabilityStore((state) => state);
  const { setPreferenceValues } = usePreferencesStore((state) => state);
  const { setOrganization, setOrganizationImage, setOrganizationLogo } = useAccountStore(
    (state) => state
  );
  const {
    setOrganizationMembers,
    setGuests,
    setAdmin,
    setIsOnlyAdmin,
    setMemberCount,
    setIsOwner,
    setOwner,
    setPendingInviteMembers,
    setPendingInviteGuests,
  } = useManageMembersStore((state) => state);

  useEffect(() => {
    if (currentMenu) setActiveMenu(currentMenu);
  }, [currentMenu]);

  useEffect(() => {
    setAdmin(data?.admin);
    setMemberCount(data?.memberCount);
    setIsOnlyAdmin(data?.isOnlyAdmin);
    const { member, preferences, careerPath } = data;

    setOrganization(data?.organisation?.name);
    setOrganizationImage(data?.organisation?.organisation_avatar);
    setOrganizationLogo(data?.organisation?.organisation_logo);
    if (member && careerPath) {
      setProfileValues({
        name: member.firstname,
        lastName: member.lastname,
        emailId: member.email,
        careerPaths: careerPath,
        profileImage: member?.avatar,
      });
    }
    if (member?.availability?.hours) {
      setHours(member.availability.hours);
    }
    if (data?.members) {
      setOrganizationMembers(data?.members);
    }

    if (data?.pendingInviteMembers) {
      setPendingInviteMembers(data?.pendingInviteMembers);
    }
    if (data?.pendingInviteGuests) {
      setPendingInviteGuests(data?.pendingInviteGuests);
    }

    if (data?.guests && Array.isArray(data?.guests)) {
      const guestDetails = data?.guests?.map((item) => {
        return {
          member: item?.guest,
          files: item?.files?.map((obj) => {
            console.log(obj);
            return {
              title: obj.title || obj.name,
              type: obj.type,
              id: obj.workspaceId || obj.file || obj._id,
              icon: obj?.icon,
            };
          }),
        };
      });
      setGuests(guestDetails);
    }
    if (data?.isOwner) {
      setIsOwner(data.isOwner);
    }

    if (data?.owner) {
      setOwner(data.owner);
    }
    if (preferences) {
      const { notifications, dashboard } = preferences;
      setPreferenceValues({
        checkboxFileInvite: notifications.email?.file_invite,
        checkboxOrganisationInvite: notifications.email?.organisation_invite,
        checkboxWorkspaceInvite: notifications.email?.workspace_invite,
        checkboxFileMention: notifications.email?.mention_file,
        checkboxLessonMention: notifications.email?.mention_lesson,
        CheckboxActivityAssign: notifications.alerts?.microtask_assign,
        CheckboxActivityDeadline: notifications.alerts?.deadline_reminders,
        CheckboxActivityInvite: notifications.alerts?.collaboration_invite,
        CheckboxActivityMention: notifications.alerts?.mentions,
        CheckboxActivityShare: notifications.alerts?.creation_doc_shared,
        CheckboxUpdateFeature: notifications.other_email?.tips_n_tricks,
        CheckboxUpdateRecommended: notifications.other_email.recommended_modules,
        CheckboxUpdateTip: notifications.other_email?.new_features,
        sketchnoteRecommendations: dashboard?.sketchnote_recommendations,
        // blogWork: dashboard?.blog_work,
        // blog: dashboard?.blog,
        // proTips: dashboard?.pro_tips,
        // proTipsWork: dashboard?.pro_tips_work,
        recentAssignedTasks: dashboard?.recent_assigned_tasks,
        keepLearning: dashboard?.keep_learning,
        // continueFiles: dashboard?.continue_files,
        favoriteFiles: dashboard?.favorite_files,
        loFi: dashboard?.loFi,
        loFiWork: dashboard?.loFi_work,
        audioMode: preferences?.audio_mode,
        workLearnMode: preferences?.workLearnMode,
        askAlex: dashboard?.ask_alex,
        learnAskAlex: dashboard?.ask_alex_learn,
        calendar: dashboard?.calendar,
        yourWorkspaces: dashboard?.your_workspaces,
        incompleteTasks: dashboard?.incomplete_tasks,
        upcomingDeadlines: dashboard?.upcoming_deadlines,
        // quickNotepad: dashboard?.quick_notepad,
        embedAnything: dashboard?.embed_anything,
        changelogWork: dashboard?.changelog_work,
        helpcenterWork: dashboard?.helpcenter_work,
        // roadmapWork: dashboard?.roadmap_work,
      });
    }
  }, [JSON.stringify(data)]);

  function handleMenuClick(value: string) {
    setActiveMenu(value);
  }

  const isActive = useCallback((item: string) => item === activeMenu, [activeMenu]);

  const menuItems = [
    {
      sectionTitle: 'Personal',
      items: [
        {
          label: 'Profile',
          value: 'profile',
        },
        {
          label: 'Preferences',
          value: 'preferences',
        },
        {
          label: 'Availability',
          value: 'availability',
        },
      ],
    },
    {
      sectionTitle: 'Organization',
      items: [
        {
          label: 'Account',
          value: 'account',
        },
        {
          label: 'Manage members',
          value: 'manageMembers',
        },
        {
          label: 'Integrations',
          value: 'integrations',
        },
        {
          label: 'Plan & Billing',
          value: 'billing',
        },
      ],
    },
  ];

  const menuItemsGuest = [
    {
      sectionTitle: 'Personal',
      items: [
        {
          label: 'Profile',
          value: 'profile',
        },
        {
          label: 'Preferences',
          value: 'preferences',
        },
      ],
    },
    {
      sectionTitle: 'Organization',
      items: [
        {
          label: 'Account',
          value: 'account',
        },
      ],
    },
  ];

  let child = <Profile onClose={onClose} />;
  let onBackBtnClick;

  switch (activeMenu) {
    case 'profile': {
      child = <Profile onClose={onClose} />;
      break;
    }
    case 'preferences': {
      child = <Preferences onClose={onClose} />;
      break;
    }
    case 'availability': {
      child = <Availability onClose={onClose} />;
      break;
    }
    case 'account': {
      child = (
        <Account
          onClose={onClose}
          plan={authData?.plan.type || ''}
          trial={authData?.plan?.trial || false}
        />
      );
      break;
    }
    case 'manageMembers': {
      child = <ManageMembers onClose={onClose} />;
      break;
    }
    case 'integrations': {
      child = <Integrations onClose={onClose} setActiveMenu={setActiveMenu} />;
      break;
    }
    case 'billing': {
      child = <Billing onClose={onClose} />;
      break;
    }
    case 'slack': {
      child = <SlackPage onClose={onClose} />;
      onBackBtnClick = () => setActiveMenu('integrations');
      break;
    }
    case 'github': {
      child = <GithubPage onClose={onClose} />;
      onBackBtnClick = () => setActiveMenu('integrations');
      break;
    }

    case 'google': {
      child = <GooglePage onClose={onClose} />;
      onBackBtnClick = () => setActiveMenu('integrations');
      break;
    }
  }

  return (
    <>
      <CommonModalWithSidebar
        width="944px"
        menuItems={memberRole === 'guest' ? menuItemsGuest : menuItems}
        isActive={isActive}
        handleMenuClick={handleMenuClick}
        open={open}
        onClose={onClose}
        onCloseBtnClick={onClose}
        headText="Settings"
        onBackBtnClick={onBackBtnClick}
      >
        {child}
      </CommonModalWithSidebar>
    </>
  );
};
